import * as THREE from 'three';
import {
  SHOOTING_STAR_POOL,
  SHOOTING_STAR_MIN_DELAY,
  SHOOTING_STAR_MAX_DELAY,
} from '../config.js';

// Pool of two-vertex Line objects used as meteor streaks. Each streak has a
// bright head vertex and a faded tail vertex; we animate both every frame.

const pool = [];
let nextSpawn = 2.5;

export function createShootingStars(scene) {
  const material = new THREE.LineBasicMaterial({
    transparent: true,
    vertexColors: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    linewidth: 1,
    fog: false,
  });
  for (let i = 0; i < SHOOTING_STAR_POOL; i++) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
    geometry.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(6), 3));
    const line = new THREE.Line(geometry, material);
    line.frustumCulled = false;
    line.visible = false;
    scene.add(line);
    pool.push({
      active: false,
      pos: new THREE.Vector3(),
      vel: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      tailLen: 0,
      color: new THREE.Color(),
      line,
    });
  }
}

function spawn() {
  const s = pool.find(x => !x.active);
  if (!s) return;

  // Start on a far shell, move roughly tangent so the streak crosses the sky
  // rather than heading straight for/away from the camera.
  const startDir = new THREE.Vector3().randomDirection();
  s.pos.copy(startDir).multiplyScalar(280);

  const velDir = new THREE.Vector3().randomDirection();
  velDir.addScaledVector(startDir, -velDir.dot(startDir)).normalize();
  s.vel.copy(velDir).multiplyScalar(120 + Math.random() * 160);

  s.maxLife = 0.7 + Math.random() * 0.7;
  s.life = s.maxLife;
  s.tailLen = 18 + Math.random() * 32;

  const warm = Math.random() < 0.25;
  s.color.setRGB(1.0, warm ? 0.82 : 0.96, warm ? 0.66 : 0.95);
  s.active = true;
  s.line.visible = true;
}

export function updateShootingStars(dt) {
  nextSpawn -= dt;
  if (nextSpawn <= 0) {
    spawn();
    nextSpawn = SHOOTING_STAR_MIN_DELAY
      + Math.random() * (SHOOTING_STAR_MAX_DELAY - SHOOTING_STAR_MIN_DELAY);
  }

  for (const s of pool) {
    if (!s.active) continue;
    s.pos.addScaledVector(s.vel, dt);
    s.life -= dt;
    if (s.life <= 0) {
      s.active = false;
      s.line.visible = false;
      continue;
    }

    // Fade in fast, out slow.
    const t = s.life / s.maxLife;
    const alpha = t < 0.2 ? (t / 0.2) : Math.min(1, t * 1.4);

    const posArr = s.line.geometry.attributes.position.array;
    const colArr = s.line.geometry.attributes.color.array;
    const velLen = s.vel.length();

    posArr[0] = s.pos.x;
    posArr[1] = s.pos.y;
    posArr[2] = s.pos.z;
    posArr[3] = s.pos.x - (s.vel.x / velLen) * s.tailLen;
    posArr[4] = s.pos.y - (s.vel.y / velLen) * s.tailLen;
    posArr[5] = s.pos.z - (s.vel.z / velLen) * s.tailLen;

    colArr[0] = s.color.r * alpha;
    colArr[1] = s.color.g * alpha;
    colArr[2] = s.color.b * alpha;
    colArr[3] = 0; colArr[4] = 0; colArr[5] = 0;

    s.line.geometry.attributes.position.needsUpdate = true;
    s.line.geometry.attributes.color.needsUpdate = true;
  }
}
